/**
 * Framework-agnostic builder that turns raw route geometry into Chart.js-ready
 * elevation-profile data.
 *
 * The important properties, shared by every elevation renderer:
 *
 *  - Points are `{x, y}` on a LINEAR distance axis. `x` is the real cumulative
 *    distance (in the user's big unit) so spacing reflects distance, not array
 *    index. Plotting on a category axis spaced by array index is what produced
 *    the "stepping" artifact on tracks with long straight segments.
 *  - Downsampling is by EQUAL DISTANCE intervals, not by array index, so 1km /
 *    2km / … land at equal pixel spacing regardless of how the raw points are
 *    distributed. The first and last real points are ALWAYS included so the
 *    x-axis max equals the true route end distance.
 *  - Elevation gain AND loss both use the shared 6m-threshold moving-baseline
 *    algorithm (see elevationGain.ts), never a naive positive-delta sum.
 */
import { distance as turfDistance } from '@turf/turf';
import {
  MeasurementPrefUtility,
} from '../helpers/MeasurementPref';
import {
  elevationGainFromCoordinates,
  elevationLossFromCoordinates,
} from './elevationGain';
import { computeSustainedGradients } from './gradient';

export interface ElevationChartPoint {
  /** Distance along route, in the user's big unit (km / mi / …). */
  x: number;
  /** Elevation, in the user's small unit (m / ft / …). */
  y: number;
}

export interface BuildElevationChartOptions {
  /** `[lon, lat, (ele)]` GeoJSON-order coordinates. */
  coordinates: number[][];
  /** Optional parallel elevation array (metres). Overrides `coordinates[i][2]`. */
  altitudes?: number[];
  /** Optional parallel cumulative-distance array (metres). Computed if omitted. */
  distances?: number[];
  /** Optional parallel per-point surface integers (see surfaces.ts). */
  surfaces?: number[];
  /** Maximum number of plotted points after equal-distance downsampling. */
  maxPoints?: number;
  /** Injected measurement preference. Defaults to the current user's. */
  measurementPref?: MeasurementPrefUtility;
  /** Threshold (metres) for the gain/loss moving-baseline algorithm. */
  elevationThresholdMeters?: number;
  /**
   * Distance window (metres) each point's steepness is averaged over — see
   * {@link computeSustainedGradients}. Defaults to 200m: short enough to still
   * separate a genuinely steep pitch from the flat approach either side of
   * it, long enough to smooth out GPS/DEM noise and single-sample jitter
   * that would otherwise flash as a ±20% spike lasting one pixel.
   */
  gradientWindowMeters?: number;
}

export interface ElevationChartData {
  /** Downsampled `{x, y}` points on the linear distance axis. */
  points: ElevationChartPoint[];
  /** Distance labels (big-unit formatted) parallel to `points`. */
  labels: string[];
  /** Per-point surface integers parallel to `points` (undefined where absent). */
  surfaces: (number | undefined)[];
  /** Latitudes parallel to `points` (for map sync). */
  lats: number[];
  /** Longitudes parallel to `points` (for map sync). */
  lons: number[];
  /** Cumulative distance in metres parallel to `points` (for map sync). */
  distancesMeters: number[];
  /**
   * Full-resolution cumulative distance in metres, parallel to the INPUT
   * `coordinates` (not decimated to `points`). Needed by callers that must
   * index into the raw `coordinates`/`altitudes` arrays directly, e.g. a
   * drag-to-select range computed on full-resolution data — pairing decimated
   * `distancesMeters` with full-resolution `coordinates` silently mismatches
   * lengths.
   */
  fullDistancesMeters: number[];
  /**
   * Signed grade % per sampled point (positive = incline, negative = decline),
   * parallel to `points`. Computed from the SAMPLED distances/elevations in
   * metres so it is unit-independent; the last point repeats the previous
   * point's grade. See gradient.ts.
   */
  gradients: number[];
  /**
   * Maximum x value (in the user's big unit) — equals the last point's x, i.e.
   * the true route end distance. Pin the chart's x-axis max to this.
   */
  maxDistance: number;
  /** True route length in metres. */
  maxDistanceMeters: number;
  /** Total ascent in metres (6m-threshold algorithm). */
  elevationGainMeters: number;
  /** Total descent in metres (6m-threshold algorithm). */
  elevationLossMeters: number;
  /** Unit symbols for axis ticks / tooltips. */
  unitLabels: { distance: string; elevation: string };
}

/**
 * Binary-search the index of the cumulative-distance array whose value is
 * closest to `target`. `cumDist` must be non-decreasing.
 */
function nearestDistanceIndex(cumDist: number[], target: number): number {
  let lo = 0;
  let hi = cumDist.length - 1;
  if (target <= cumDist[lo]) return lo;
  if (target >= cumDist[hi]) return hi;

  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (cumDist[mid] < target) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  // lo is the first index with cumDist[lo] >= target; compare against lo-1.
  if (lo > 0 && Math.abs(cumDist[lo - 1] - target) <= Math.abs(cumDist[lo] - target)) {
    return lo - 1;
  }
  return lo;
}

/**
 * Pick `maxPoints` indices spaced evenly along cumulative distance, always
 * including the first and last real points. Returns a sorted, de-duplicated
 * array of source indices.
 */
export function equalDistanceSampleIndices(
  cumDist: number[],
  maxPoints: number
): number[] {
  const n = cumDist.length;
  if (n === 0) return [];
  if (n <= maxPoints || maxPoints < 2) {
    return Array.from({ length: n }, (_, i) => i);
  }

  const total = cumDist[n - 1];
  const indices = new Set<number>();
  indices.add(0);
  indices.add(n - 1);

  if (total > 0) {
    for (let i = 0; i < maxPoints; i++) {
      const target = (i / (maxPoints - 1)) * total;
      indices.add(nearestDistanceIndex(cumDist, target));
    }
  }

  return Array.from(indices).sort((a, b) => a - b);
}

/**
 * Build Chart.js-ready elevation-profile data from route geometry.
 */
export function buildElevationChartData(
  options: BuildElevationChartOptions
): ElevationChartData {
  const {
    coordinates,
    altitudes,
    distances,
    surfaces,
    maxPoints = 300,
    elevationThresholdMeters = 6,
    gradientWindowMeters = 200,
  } = options;

  const measurementPref =
    options.measurementPref ?? MeasurementPrefUtility.preference();

  const unitLabels = {
    distance: measurementPref.kilometerSymbol(),
    elevation: measurementPref.meterSymbol(),
  };

  const emptyResult: ElevationChartData = {
    points: [],
    labels: [],
    surfaces: [],
    lats: [],
    lons: [],
    distancesMeters: [],
    fullDistancesMeters: [],
    gradients: [],
    maxDistance: 0,
    maxDistanceMeters: 0,
    elevationGainMeters: 0,
    elevationLossMeters: 0,
    unitLabels,
  };

  if (!coordinates || coordinates.length === 0) {
    return emptyResult;
  }

  const n = coordinates.length;

  // Full-resolution elevation (metres) for each coordinate.
  const eleAt = (i: number): number => {
    if (altitudes && altitudes[i] != null) return altitudes[i];
    return coordinates[i][2] ?? 0;
  };

  // Full-resolution cumulative distance (metres).
  const cumDist: number[] = new Array(n);
  if (distances && distances.length === n) {
    for (let i = 0; i < n; i++) cumDist[i] = distances[i];
  } else {
    cumDist[0] = 0;
    for (let i = 1; i < n; i++) {
      const segment = turfDistance(
        coordinates[i - 1].slice(0, 2),
        coordinates[i].slice(0, 2),
        { units: 'meters' }
      );
      cumDist[i] = cumDist[i - 1] + segment;
    }
  }

  // Full-resolution [lng, lat, ele] for the shared gain/loss algorithm.
  const fullCoords: number[][] = new Array(n);
  for (let i = 0; i < n; i++) {
    fullCoords[i] = [coordinates[i][0], coordinates[i][1], eleAt(i)];
  }

  const elevationGainMeters = elevationGainFromCoordinates(
    fullCoords,
    elevationThresholdMeters
  );
  const elevationLossMeters = elevationLossFromCoordinates(
    fullCoords,
    elevationThresholdMeters
  );

  // Downsample by equal distance intervals.
  const sampleIndices = equalDistanceSampleIndices(cumDist, maxPoints);

  const points: ElevationChartPoint[] = [];
  const labels: string[] = [];
  const outSurfaces: (number | undefined)[] = [];
  const lats: number[] = [];
  const lons: number[] = [];
  const distancesMeters: number[] = [];

  for (const idx of sampleIndices) {
    const distMeters = cumDist[idx];
    const distKm = distMeters / 1000;
    const eleMeters = eleAt(idx);
    points.push({
      x: measurementPref.bigUnitValue(distKm),
      y: measurementPref.smallUnitValue(eleMeters),
    });
    labels.push(measurementPref.bigUnitFormatter(distKm, false));
    outSurfaces.push(surfaces ? surfaces[idx] : undefined);
    lats.push(coordinates[idx][1]);
    lons.push(coordinates[idx][0]);
    distancesMeters.push(distMeters);
  }

  // Signed per-point grade %, averaged over gradientWindowMeters of
  // FULL-RESOLUTION track (not sample-to-sample, which is only as wide as the
  // downsampling interval and reads GPS/DEM jitter as spurious spikes).
  const fullElevationsMeters = fullCoords.map((c) => c[2]);
  const gradients = computeSustainedGradients(
    cumDist,
    fullElevationsMeters,
    sampleIndices,
    gradientWindowMeters
  );

  const maxDistanceMeters = cumDist[n - 1];
  const maxDistance = points.length ? points[points.length - 1].x : 0;

  return {
    points,
    labels,
    surfaces: outSurfaces,
    lats,
    lons,
    distancesMeters,
    fullDistancesMeters: cumDist,
    gradients,
    maxDistance,
    maxDistanceMeters,
    elevationGainMeters,
    elevationLossMeters,
    unitLabels,
  };
}
